import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Wrench, Search, ShieldCheck, Car, CreditCard, Layers, CheckCircle, Phone, ChevronRight, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceIcons = {
  'Réparation Automobile': Wrench,
  'Diagnostic Automobile': Search,
  'Contrôle Technique': ShieldCheck,
  'Vente de Pièces Auto': Layers,
  'Plaques d\'Immatriculation': CreditCard,
  'Accessoires Automobile': Car,
};

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/services`);
        setServices(res.data);
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };
    fetchServices();
  }, []);

  return (
    <div style={{ background: 'var(--bg-primary)', fontFamily: 'var(--font-body)' }} data-testid="services-page">
      {/* Hero - compact */}
      <section className="relative py-16 lg:py-20 overflow-hidden" style={{ background: 'var(--bg-surface)' }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1615906655593-ad0386982a0f?auto=format&fit=crop&w=1920&q=30')`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="section-label">Nos prestations</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3 mb-4 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Nos Services <span className="text-[#DC2626]">Automobiles</span>
            </h1>
            <p className="text-zinc-400 max-w-xl">
              MCA CAR vous propose une gamme complète de services pour l'entretien, la réparation et l'équipement de votre véhicule.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => {
              const Icon = serviceIcons[service.name] || Wrench;
              const isMain = index === 0;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`service-card group ${isMain ? 'md:col-span-2 lg:row-span-2' : ''}`}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className={`flex items-center justify-center w-14 h-14 mb-5 border border-white/10 ${isMain ? 'bg-[#DC2626]' : 'bg-white/5'}`}>
                    <Icon size={26} className={isMain ? 'text-white' : 'text-[#DC2626]'} />
                  </div>
                  <h3 className={`font-bold text-white mb-3 ${isMain ? 'text-2xl lg:text-3xl' : 'text-xl'}`}>{service.name}</h3>
                  <p className={`text-zinc-500 leading-relaxed mb-5 ${isMain ? 'text-base max-w-md' : 'text-sm'}`}>{service.description}</p>
                  {service.features && (
                    <ul className="space-y-2.5">
                      {service.features.map((feature, fi) => (
                        <li key={fi} className="flex items-start gap-2 text-sm text-zinc-400">
                          <CheckCircle size={14} className="text-[#DC2626] flex-shrink-0 mt-0.5" /> {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 lg:py-16 border-t border-white/5" style={{ background: 'var(--bg-surface)' }}>
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">Besoin d'une intervention ?</h2>
            <p className="text-zinc-500 mt-1 text-sm">Devis gratuit et sans engagement. Réponse sous 24h.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/contact" className="group inline-flex items-center gap-2 px-6 py-3.5 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C]" data-testid="services-cta-devis">
              Demander un devis <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="tel:+33613851946" className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white font-semibold text-sm transition-all hover:bg-white/5" data-testid="services-cta-phone">
              <Phone size={16} /> Appeler
            </a>
          </div>
        </div>
      </section>

      {/* Mobile Sticky Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 flex" style={{ background: 'var(--bg-primary)' }}>
        <a href="tel:+33613851946" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#DC2626] text-white font-semibold text-sm">
          <Phone size={16} /> Appeler
        </a>
        <a href="https://wa.me/33613851946" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 text-white font-semibold text-sm">
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </div>
  );
};

export default Services;
